import React, { useEffect, useRef, useState } from 'react';
import { imageUrl } from '../../../api/endpoints';
import { IconBalance } from '../../../components/icons/AnalyzerIcons';
import useScanCredits from '../../../hooks/useScanCredits';
import AwaitingTeamCard from './AwaitingTeamCard';
import BrandNameForm from './BrandNameForm';
import CandidateSheet from './CandidateSheet';
import LabelCapture from './LabelCapture';
import PasteIngredients from './PasteIngredients';
import SideEntry from './SideEntry';
import { isTranslatedLabel, TRANSLATED_LABEL_NOTE } from '../../../utils/labelSource';
import { UNVERIFIED_STATUS } from './reducer';
import type { LabelMeta } from '../../../types/scanV2';
import type { SideProduct, SideState } from './reducer';
import { useProductSideFlow, type ProductSideFlow as Flow, type SideFlowContext } from './useProductSideFlow';

export const DUPLICATE_MESSAGE = 'Pick a different product: this one is already on the other side.';
export const CREDIT_RETURNED_MESSAGE = 'Your credit was returned.';
export const PENDING_MESSAGE =
  'Our team is still adding the ingredients of this product. Pick another product to compare now.';

export interface ProductSideFlowProps {
  side: 'a' | 'b';
  context: SideFlowContext;
  /** "Product A" / "Product B". */
  label: string;
  /** Fires with the product on entering `ready`, and null on leaving it. */
  onChange: (product: SideProduct | null) => void;
  initial?: SideProduct;
  /** Barcode to prefill in the entry (from a link), not identified until the user asks. */
  initialBarcode?: string;
  /** The other side's product: picking it again shows the duplicate guard. */
  disabledProductId?: number;
  /** The comparison answered 409 for this side: its ingredients are pending. */
  pendingMarked?: boolean;
  /** Extra content under a ready product (e.g. the compat usage control). */
  footer?: React.ReactNode;
}

export const UNVERIFIED_CARD_NOTE = 'Ingredients not confirmed: the AI gave this list without a source.';

function ReadyCard({ side, label, product, canNotMine, onNotMine, onClear, onVerify }: {
  side: 'a' | 'b';
  label: string;
  product: SideProduct;
  canNotMine: boolean;
  onNotMine: () => void;
  onClear: () => void;
  onVerify: () => void;
}) {
  const src = product.imageUrl ? imageUrl(product.imageUrl) : null;
  const fromLabel = product.ingredientsStatus === 'pending_review';
  const unverified = product.ingredientsStatus === UNVERIFIED_STATUS;
  return (
    <div className={`compare-product-summary compare-product-summary--${side}`} style={{ padding: 'var(--s-3)', display: 'flex', gap: 'var(--s-3)', alignItems: 'center' }}>
      <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--c-cream-weak)', display: 'grid', placeItems: 'center' }}>
        {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <IconBalance />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
        {product.brand && <div className="t-small t-mute">{product.brand}</div>}
        {fromLabel && (
          <div className="t-small t-mute">
            {isTranslatedLabel(product.labelSource) ? TRANSLATED_LABEL_NOTE : 'Ingredients read from your label'}
          </div>
        )}
        {unverified && (
          <div className="stack-1" style={{ marginTop: 'var(--s-1)' }}>
            <span className="pill pill--coral" style={{ alignSelf: 'flex-start' }}>Not confirmed</span>
            <span className="t-small t-mute">{UNVERIFIED_CARD_NOTE}</span>
            {product.scanId && (
              <button type="button" className="t-small" onClick={onVerify} style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', alignSelf: 'flex-start' }}>
                Photograph the ingredients to confirm
              </button>
            )}
          </div>
        )}
        {canNotMine && (
          <button type="button" className="t-small" onClick={onNotMine} style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}>
            Not my product?
          </button>
        )}
      </div>
      <button type="button" onClick={onClear} className="btn btn--ghost btn--sm" aria-label={`Remove ${label}`}>✕</button>
    </div>
  );
}

/** The brand and name the next label step sends (from the photo or the user), with an Edit form. */
function MetaSummary({ side, label, meta, onEdit }: {
  side: 'a' | 'b';
  label: string;
  meta?: LabelMeta;
  onEdit: (meta: LabelMeta) => void;
}) {
  const [editing, setEditing] = useState(false);
  if (!meta) return null;
  if (editing) {
    return (
      <BrandNameForm
        side={side}
        initial={meta}
        onSubmit={(next) => { setEditing(false); onEdit(next); }}
        onCancel={() => { setEditing(false); }}
      />
    );
  }
  const title = [meta.brand, meta.name, meta.variant].filter(Boolean).join(' ');
  return (
    <div className="flex items-center justify-between gap-2" data-testid="label-meta">
      <span className="t-small" style={{ minWidth: 0, color: 'var(--ink)' }}>{title}</span>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => { setEditing(true); }}
        aria-label={`Edit brand and name for ${label}`}
      >
        Edit
      </button>
    </div>
  );
}

function StepBody({ props, flow, identifyCost, isPlusOrPremium, focusEntry, clear }: {
  props: ProductSideFlowProps;
  flow: Flow;
  identifyCost: number | null;
  isPlusOrPremium: boolean;
  /** The user cleared the side: the entry takes focus. */
  focusEntry: boolean;
  clear: () => void;
}) {
  const { side, label } = props;
  const s: SideState = flow.state;
  switch (s.step) {
    case 'empty':
      return (
        <SideEntry
          side={side}
          label={label}
          initialBarcode={s.barcode ?? props.initialBarcode}
          identifyCost={identifyCost}
          isPlusOrPremium={isPlusOrPremium}
          excludedProductId={props.disabledProductId}
          focusOnMount={focusEntry}
          onBarcode={(code) => { void flow.identifyBarcode(code); }}
          onPhoto={(file) => { void flow.identifyPhoto(file); }}
          onFavorite={flow.pickFavorite}
        />
      );
    case 'identifying':
      return <p className="t-small t-mute" role="status" style={{ margin: 0 }}>Finding your product…</p>;
    case 'candidates':
    case 'confirming':
      return (
        <CandidateSheet
          candidates={s.candidates}
          confirmingId={s.step === 'confirming' ? s.candidateId : undefined}
          onConfirm={(id) => { void flow.confirm(id); }}
          onNoneOfThese={() => { void flow.rejectAll(); }}
          onCancel={clear}
          label={label}
        />
      );
    case 'ready': {
      const duplicate = props.disabledProductId !== undefined && props.disabledProductId === s.product.productId;
      const warning = duplicate ? DUPLICATE_MESSAGE : props.pendingMarked ? PENDING_MESSAGE : null;
      return (
        <>
          <ReadyCard side={side} label={label} product={s.product} canNotMine={s.canNotMine} onNotMine={() => { void flow.notMine(); }} onClear={clear} onVerify={flow.verifyWithLabel} />
          {warning && <div className="notice notice--error" role="alert">{warning}</div>}
          {props.footer}
        </>
      );
    }
    case 'label':
      return (
        <>
          <MetaSummary side={side} label={label} meta={flow.rememberedMeta} onEdit={flow.editMeta} />
          <LabelCapture
            side={side}
            parts={s.parts}
            attemptsLeft={s.attemptsLeft}
            hint={s.hint}
            reason={s.reason}
            found={s.found}
            reading={s.reading}
            error={s.error}
            onAddPart={flow.addPart}
            onRemovePart={flow.removePart}
            onRead={() => { void flow.readLabel(); }}
            {...(s.verifying ? { onCancelVerify: flow.cancelVerify } : {})}
          />
        </>
      );
    case 'fallback':
      return (
        <>
          <MetaSummary side={side} label={label} meta={flow.rememberedMeta} onEdit={flow.editMeta} />
          <PasteIngredients
            side={side}
            busy={s.busy}
            error={s.error}
            onPaste={(text) => { void flow.pasteText(text); }}
            {...(s.verifying ? {} : { onSendToTeam: () => { void flow.sendToTeam({}); } })}
          />
          {s.verifying && (
            <button type="button" className="btn btn--ghost btn--sm justify-center" disabled={s.busy} onClick={flow.cancelVerify}>
              Keep the unconfirmed list
            </button>
          )}
        </>
      );
    case 'needsBrandName':
      return <BrandNameForm side={side} initial={flow.rememberedMeta} onSubmit={(meta) => { void flow.submitBrandName(meta); }} onCancel={flow.cancel} />;
    case 'awaitingTeam':
      return <AwaitingTeamCard onPickAnother={clear} solo={props.context === 'solo'} />;
    case 'error':
      return (
        <div className="stack-2">
          <div className="notice notice--error" role="alert">{s.message}</div>
          <div className="flex gap-2">
            <button type="button" className="btn btn--primary btn--sm" onClick={flow.retry}>Try again</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={clear}>Start over</button>
          </div>
        </div>
      );
  }
}

/**
 * One product picked through scan v2: barcode, photo or favorites →
 * candidates / verified hit → label fallback. The compare and compat screens
 * render it twice (sides A and B); the solo scan renders it once, without the
 * side badge.
 */
export default function ProductSideFlow(props: ProductSideFlowProps) {
  const { side, context, label, initial, onChange } = props;
  const flow = useProductSideFlow(context, initial);
  const { cost: identifyCost, isPlusOrPremium } = useScanCredits('product_barcode');
  // The control the user pressed to clear the side unmounts with it: hand focus to the entry.
  const [focusEntry, setFocusEntry] = useState(false);
  const clear = () => {
    setFocusEntry(true);
    flow.clear();
  };

  const readyProduct = flow.state.step === 'ready' ? flow.state.product : null;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const reportedRef = useRef<SideProduct | null>(null);
  useEffect(() => {
    if (reportedRef.current === readyProduct) return;
    reportedRef.current = readyProduct;
    onChangeRef.current(readyProduct);
  }, [readyProduct]);
  // Leaving the screen (or a swap remount) frees the pair slot.
  useEffect(() => () => {
    if (reportedRef.current === null) return;
    reportedRef.current = null;
    onChangeRef.current(null);
  }, []);

  const solo = context === 'solo';
  const note = flow.state.step === 'empty'
    ? (solo ? 'Scan the barcode, take a photo or pick a favorite' : 'Choose how to add a product')
    : flow.state.step === 'ready' ? (solo ? 'Found' : 'Selected') : 'Finding your product';

  return (
    <section className={`compare-slot compare-slot--${side}`} aria-labelledby={`compare-slot-${side}-title`}>
      <div className="compare-slot__header">
        {!solo && <span className={`compare-side-badge compare-side-badge--${side}`} aria-hidden>{side.toUpperCase()}</span>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 id={`compare-slot-${side}-title`} className="t-small" style={{ margin: 0, fontWeight: 700 }}>{label}</h2>
          <p className="t-small t-mute" style={{ margin: 0 }}>{note}</p>
        </div>
      </div>
      {flow.creditReturned && (
        <div className="notice" role="status" data-testid="credit-returned">{CREDIT_RETURNED_MESSAGE}</div>
      )}
      <StepBody props={props} flow={flow} identifyCost={identifyCost} isPlusOrPremium={isPlusOrPremium} focusEntry={focusEntry} clear={clear} />
    </section>
  );
}
